import isEmpty from 'lodash/isEmpty';
import dynamic from 'next/dynamic';
import { memo } from 'react';

import HeadContent from '@shared-oneflare-components/HeadContent';
import { AnalyticsPage } from 'lib/analytics/constants';
import { publicRuntimeConfig } from 'lib/utils/Environment';
import PageHeader from 'shared/components/PageHeader/PageHeader';
import SimpleHero from 'shared/components/SimpleHero';
import { CONTENT_HUB_WP_PATH } from 'shared/utils/constants';
import { IWpInspirationDataModel } from 'types/oneflare.com.au/inspiration';
import type { IGetAllPostsQueryDataModel, IWpGetCategoryDataModel } from 'types/shared';

const Footer = dynamic(() => import('shared/components/Footer'));
const LazyLoadedSection = dynamic(() => import('shared/components/LazyLoadedSection/LazyLoadedSection'), { ssr: false });
const Navigation = dynamic(() => import('shared/components/Navigation/Navigation'));
const CostGuidesStyled = dynamic(() => import('./styled/CostGuidesStyled'));
const FooterCTA = dynamic(() => import('../shared/components/FooterCTA/FooterCTA'));
const ArticleListStatic = dynamic(() => import('../shared/components/ArticleListSection/ArticleListStatic'));

const StaticArticles = ({ getAllPosts, subCategoryData, slug }) => {
  return getAllPosts.length > 0 && subCategoryData && subCategoryData.map((category, index) => {    
    const postData = !isEmpty(getAllPosts[index]) && getAllPosts[index].getAllPost.map(({
      modified, link, title, acf, categoryParentData
    }) => {
      const imageSizes = acf?.pageSetup?.thumbnail?.sizes;
      const thumbnail = imageSizes?.medium;
      const altText = acf?.pageSetup?.thumbnail?.alt;
      const subTitle = acf?.pageSetup?.articleSubtitle;
      const parentCategoryName = categoryParentData?.name;
      return {
        thumbnail,
        title: title.rendered,
        url: link.replace(CONTENT_HUB_WP_PATH, ''),
        subtitle: subTitle,
        altText,
        timestamp: modified,
        label: parentCategoryName
      };
    });

    if (isEmpty(postData)) return null;

    return (
      <ArticleListStatic
        sectionTitle={category.name.replace(/&amp;/g, '&')}
        titleLink={`${slug}/${category.slug}`}
        postData={postData}
        key={category.slug}
        variant={{
          imageSizing: 'standard',
          layout: 'horizontal',
          showSubTitle: false,
          showThumbNail: true,
          showTimeStamp: true,
          showTags: false,
          titleHeight: true,
          enablePagination: false,
          assetsPerPage: 10
        }}
      />
    );
  });
};

type Props = {
  pageUrl?: string;
  params?: {
    slug: string;
    staticRender: boolean;
  };
  getAllPosts: Array<IGetAllPostsQueryDataModel>;
  wpData: IWpInspirationDataModel;
  getCategoryPrefix: Omit<IWpGetCategoryDataModel, 'slug'>;
  subCategoryData: Array<IWpGetCategoryDataModel>;
};

function Category(props: Partial<Props>) {
  const {
    pageUrl,
    params,
    wpData,
    getCategoryPrefix,
    subCategoryData,
    getAllPosts
  } = props;
  const pageTitle = getCategoryPrefix?.acf?.categorySetup?.pageTitle;
  const metaDescription = getCategoryPrefix?.acf?.categorySetup?.metaDescription;
  const categoryTitle = getCategoryPrefix?.name;
  const heroBackground = getCategoryPrefix?.acf?.heroSetup?.backgroundImage?.url;
  const heroIntro = getCategoryPrefix?.acf?.heroSetup?.intro;

  return (
    <>
      <HeadContent
        pageUrl={`${publicRuntimeConfig.SITE_URL}${pageUrl}`}
        pageTitle={pageTitle}
        metaDescription={metaDescription}
        pageShareImg={heroBackground}
      />
      <PageHeader
        jobFormInitiatedPageName={AnalyticsPage.InspirationCentre}
        initiatedAuthBeginPageName={AnalyticsPage.InspirationCentre}
      />
      <Navigation
        category=""
        noSearchBar={false}
        className="smUp"
        data={wpData?.topLevelCategoryData}
        pageName="Inspiration"
        path="inspiration"
      />
      <SimpleHero
        className=""
        parentCategorySlug=""
        jobFormCategoryId={null}
        title={categoryTitle}
        introText={heroIntro}
        bgImg={heroBackground}
        jobFormInitiatedPageName={AnalyticsPage.InspirationCentre}
      />
      <StaticArticles
        getAllPosts={getAllPosts}
        subCategoryData={subCategoryData}
        slug={params.slug}
      />
      <CostGuidesStyled
        heading="How much will your job cost?"
        subHeading="The Oneflare Cost Guide Centre is your one-stop shop to help you set your budget; from smaller tasks to larger projects."
        showSeeMoreButton
        isBot={params.staticRender}
      />
      <FooterCTA />
      <LazyLoadedSection style={{ minHeight: '228px' }} autoLoad={params.staticRender}>
        <Footer />
      </LazyLoadedSection>
    </>
  );
}

export default memo(Category);
